// Applies the product-level minimum order quantity (MOQ) to the cart markup.
// Business rules live in cfy-quantity-rules.js; this file only reads the DOM,
// calls those rules and writes the result back (stepper floors, status text, checkout state).

// Collapsible parent row of products with several variant lines.
// The set is the single source of truth: the drawer markup is replaced after every
// cart change, so the DOM is rebuilt collapsed and then reflects this set.
const expandedProductIds = new Set();

function toggleProductExpanded(productId) {
  if (expandedProductIds.has(productId)) {
    expandedProductIds.delete(productId);
    return;
  }

  expandedProductIds.add(productId);
}

// Called before the cart markup is rendered so a just-added variant is visible, not hidden in a collapsed group.
function expandCartProduct(productId) {
  expandedProductIds.add(String(productId));
}

// Highlights the whole group (not only the added color line) as just added.
// Runs on the detached markup, before it replaces the drawer content. Single-line products keep the line highlight.
function markCartProductJustAdded(itemsElement, productId) {
  const productGroupElement = itemsElement.querySelector(`.js-cart-product-group[data-product-id="${productId}"]`);

  if (!productGroupElement || !productGroupElement.querySelector('.js-cart-group-toggle')) {
    return;
  }

  productGroupElement.classList.add('cart__product-group--just-added');
}

function clearCartProductJustAdded(rootElement) {
  rootElement.querySelectorAll('.js-cart-product-group').forEach((productGroupElement) => {
    productGroupElement.classList.remove('cart__product-group--just-added');
  });
}

function renderProductExpanded(productGroupElement) {
  const toggleButton = productGroupElement.querySelector('.js-cart-group-toggle');

  if (!toggleButton) {
    return;
  }

  const isExpanded = expandedProductIds.has(productGroupElement.dataset.productId);

  productGroupElement.querySelector('.js-cart-group-lines').hidden = !isExpanded;
  toggleButton.setAttribute('aria-expanded', String(isExpanded));
  toggleButton.textContent = isExpanded ? toggleButton.dataset.textHide : toggleButton.dataset.textViewAll;
}

function bindProductToggle(productGroupElement) {
  const toggleButton = productGroupElement.querySelector('.js-cart-group-toggle');

  if (!toggleButton) {
    return;
  }

  toggleButton.addEventListener('click', () => {
    toggleProductExpanded(productGroupElement.dataset.productId);
    renderProductExpanded(productGroupElement);
  });
}

function readCartLines(productGroupElement) {
  const productId = Number(productGroupElement.dataset.productId);

  return Array.from(productGroupElement.querySelectorAll('.js-cart-line')).map((lineElement) => {
    const quantityInput = lineElement.querySelector('.js-qty__num');

    return {
      productId: productId,
      lineKey: lineElement.dataset.key,
      // defaultValue is the quantity the server rendered; value may already hold what the shopper typed.
      quantity: Number.parseInt(quantityInput.defaultValue, 10),
      quantityInput: quantityInput,
      stepperElement: lineElement.querySelector('.js-qty__wrapper'),
      minusButton: lineElement.querySelector('.js-qty__adjust--minus')
    };
  });
}

function applyLineFloors(cartLines, minimumOrderQuantity, productTotalQuantity) {
  cartLines.forEach((cartLine) => {
    const lineFloor = resolveLineQuantityFloor(minimumOrderQuantity, productTotalQuantity, cartLine.quantity);
    const isAtFloor = lineFloor > 0 && cartLine.quantity <= lineFloor;

    cartLine.quantityInput.setAttribute('min', String(lineFloor));
    cartLine.minusButton.disabled = isAtFloor;
    cartLine.minusButton.classList.toggle('is-disabled', isAtFloor);
    cartLine.minusButton.setAttribute('aria-disabled', isAtFloor ? 'true' : 'false');
    const isMinimumReached = isAtFloor && isLineFloorCausedByMinimum(minimumOrderQuantity, productTotalQuantity);

    // The data attribute is the state JS reads back; the modifier class is styling only.
    cartLine.stepperElement.dataset.minimumReached = String(isMinimumReached);
    cartLine.stepperElement.classList.toggle('cart__qty-stepper--minimum-reached', isMinimumReached);
  });
}

// Disabled buttons receive no pointer events, so the click lands on the stepper itself.
// Without this nudge a shopper who taps a disabled minus gets no explanation on touch screens.
const MINIMUM_ATTENTION_DURATION_MS = 1200;
const minimumAttentionTimerIds = new WeakMap();

function highlightMinimumStatus(statusElement) {
  window.clearTimeout(minimumAttentionTimerIds.get(statusElement));
  statusElement.classList.add('cart__product-group-status--attention');

  const timerId = window.setTimeout(() => {
    statusElement.classList.remove('cart__product-group-status--attention');
  }, MINIMUM_ATTENTION_DURATION_MS);

  minimumAttentionTimerIds.set(statusElement, timerId);
}

function bindMinimumAttention(cartLines, statusElement) {
  if (!statusElement) {
    return;
  }

  cartLines.forEach((cartLine) => {
    cartLine.stepperElement.addEventListener('click', (clickEvent) => {
      const isMinimumReached = cartLine.stepperElement.dataset.minimumReached === 'true';
      const isEnabledControl = clickEvent.target.closest('.js-qty__adjust--plus, .js-qty__num');

      if (!isMinimumReached || isEnabledControl) {
        return;
      }

      highlightMinimumStatus(statusElement);
    });
  });
}

function renderProductGroupStatus(statusElement, minimumOrderQuantity, productTotalQuantity, minimumStatus) {
  if (!statusElement) {
    return;
  }

  const statusText = minimumStatus.isMet
    ? statusElement.dataset.textProgress
      .replace('__CURRENT__', String(productTotalQuantity))
      .replace('__MINIMUM__', String(minimumOrderQuantity))
    : statusElement.dataset.textMissing.replace('__MISSING__', String(minimumStatus.missingQuantity));

  statusElement.textContent = statusText;
  statusElement.classList.toggle('cart__product-group-status--error', !minimumStatus.isMet);
}

// Informational only: the real checkout discount comes from a Shopify Function, so no price changes here.
function renderProductDiscountStatus(productGroupElement, productTotalQuantity) {
  const discountStatusElement = productGroupElement.querySelector('.js-cart-product-discount-status');

  if (!discountStatusElement) {
    return;
  }

  const normalizedTiers = normalizeVolumeDiscounts(productGroupElement.dataset.volumeDiscounts);
  const discountMessage = describeCartDiscountMessage(normalizedTiers, productTotalQuantity);

  if (!discountMessage) {
    discountStatusElement.textContent = '';
    return;
  }

  const messageTemplates = {
    'next-only': productGroupElement.dataset.textDiscountNextOnly,
    'applied-and-next': productGroupElement.dataset.textDiscountAppliedAndNext,
    'applied': productGroupElement.dataset.textDiscountApplied
  };

  discountStatusElement.textContent = messageTemplates[discountMessage.kind]
    .replace('__PERCENTAGE__', String(discountMessage.activePercentage))
    .replace('__MISSING__', String(discountMessage.missingQuantity))
    .replace('__NEXT_PERCENTAGE__', String(discountMessage.nextPercentage));
}

function setCheckoutBlocked(checkoutButtons, isBlocked) {
  checkoutButtons.forEach((checkoutButton) => {
    checkoutButton.disabled = isBlocked;
    checkoutButton.setAttribute('aria-disabled', isBlocked ? 'true' : 'false');
  });
}

// A confirmed product removal rebuilds the whole cart markup, which would drop focus to <body>.
// The flag survives that rebuild and is consumed by the next applyCartProductRules call.
let shouldRestoreFocusAfterRender = false;

function restoreCartFocus(cartFormElement) {
  shouldRestoreFocusAfterRender = false;

  // Focus the form, not an input: an input would open the mobile keyboard and the first one may sit in a collapsed group.
  cartFormElement.setAttribute('tabindex', '-1');
  cartFormElement.focus({ preventScroll: true });
}

// Must run before the quantity selectors are created: they read the min attribute once.
function applyCartProductRules(cartFormElement) {
  let hasUnmetMinimum = false;

  cartFormElement.querySelectorAll('.js-cart-product-group').forEach((productGroupElement) => {
    bindProductToggle(productGroupElement);
    renderProductExpanded(productGroupElement);

    const minimumOrderQuantity = Number.parseInt(productGroupElement.dataset.minimumOrderQuantity, 10);
    const cartLines = readCartLines(productGroupElement);
    const productTotalQuantity = sumProductQuantity(cartLines, productGroupElement.dataset.productId);
    const minimumStatus = evaluateProductMinimum(minimumOrderQuantity, productTotalQuantity);

    const statusElement = productGroupElement.querySelector('.js-cart-product-group-status');

    applyLineFloors(cartLines, minimumOrderQuantity, productTotalQuantity);
    bindMinimumAttention(cartLines, statusElement);
    renderProductGroupStatus(statusElement, minimumOrderQuantity, productTotalQuantity, minimumStatus);

    renderProductDiscountStatus(productGroupElement, productTotalQuantity);

    if (!minimumStatus.isMet) {
      hasUnmetMinimum = true;
    }
  });

  setCheckoutBlocked(Array.from(cartFormElement.querySelectorAll('.js-cart-checkout')), hasUnmetMinimum);

  if (shouldRestoreFocusAfterRender) {
    restoreCartFocus(cartFormElement);
  }
}

// Confirmation before a shopper action removes a product by leaving it under its MOQ.
// One native <dialog> per cart form; it lives outside the replaced product markup.
function createConfirmDialog() {
  const dialogElement = document.createElement('dialog');
  dialogElement.className = 'cart__confirm-dialog js-cart-confirm-dialog';

  const messageElement = document.createElement('p');
  messageElement.className = 'cart__confirm-message js-cart-confirm-message';

  const cancelButton = document.createElement('button');
  cancelButton.type = 'button';
  cancelButton.className = 'btn btn--secondary cart__confirm-button js-cart-confirm-cancel';
  cancelButton.addEventListener('click', () => dialogElement.close('cancel'));

  const confirmButton = document.createElement('button');
  confirmButton.type = 'button';
  confirmButton.className = 'btn cart__confirm-button js-cart-confirm-accept';
  confirmButton.addEventListener('click', () => dialogElement.close('confirm'));

  // The header cart closes on Escape keyup. Keeping the keyup inside the dialog makes
  // Escape mean "Cancel" only, instead of also closing the drawer behind it.
  dialogElement.addEventListener('keydown', (keyEvent) => {
    if (keyEvent.key === 'Escape') {
      keyEvent.preventDefault();
    }
  });
  dialogElement.addEventListener('keyup', (keyEvent) => {
    if (keyEvent.key === 'Escape') {
      keyEvent.stopPropagation();
      dialogElement.close('cancel');
    }
  });

  const actionsElement = document.createElement('div');
  actionsElement.className = 'cart__confirm-actions';
  actionsElement.append(cancelButton, confirmButton);

  dialogElement.append(messageElement, actionsElement);

  return dialogElement;
}

function findOrCreateConfirmDialog(cartFormElement) {
  const existingDialog = cartFormElement.querySelector('.js-cart-confirm-dialog');

  if (existingDialog) {
    return existingDialog;
  }

  const dialogElement = createConfirmDialog();
  cartFormElement.append(dialogElement);

  return dialogElement;
}

function buildRemovalMessage(productGroupElement, lineCount, productTotalQuantity) {
  const productTitle = productGroupElement.dataset.productTitle;
  const messageTemplate = resolveRemovalMessageKind(lineCount) === 'several-lines'
    ? productGroupElement.dataset.textRemoveMessageWithColors
    : productGroupElement.dataset.textRemoveMessage;

  // Replacer functions keep "$" sequences in a merchant-written title from being read as replacement patterns.
  return messageTemplate
    .replace('__COLORS__', () => String(lineCount))
    .replace('__UNITS__', () => String(productTotalQuantity))
    .replace('__TITLE__', () => productTitle);
}

// Resolves true only when the shopper picks "Remove"; Escape and "Keep it" resolve false.
function askToConfirmProductRemoval(cartFormElement, productGroupElement, lineCount, productTotalQuantity) {
  const dialogElement = findOrCreateConfirmDialog(cartFormElement);

  // textContent only: the product title is merchant data and must never be parsed as HTML.
  dialogElement.querySelector('.js-cart-confirm-message').textContent =
    buildRemovalMessage(productGroupElement, lineCount, productTotalQuantity);
  dialogElement.querySelector('.js-cart-confirm-accept').textContent = productGroupElement.dataset.textRemoveConfirm;
  dialogElement.querySelector('.js-cart-confirm-cancel').textContent = productGroupElement.dataset.textRemoveCancel;
  dialogElement.returnValue = '';

  return new Promise((resolve) => {
    dialogElement.addEventListener('close', () => resolve(dialogElement.returnValue === 'confirm'), { once: true });
    dialogElement.showModal();
  });
}

// Puts back what the shopper typed and re-applies the stepper state derived from it.
function restoreProductQuantities(productGroupElement) {
  const cartLines = readCartLines(productGroupElement);
  const minimumOrderQuantity = Number.parseInt(productGroupElement.dataset.minimumOrderQuantity, 10);
  const productTotalQuantity = sumProductQuantity(cartLines, productGroupElement.dataset.productId);

  cartLines.forEach((cartLine) => {
    cartLine.quantityInput.value = cartLine.quantityInput.defaultValue;
  });
  applyLineFloors(cartLines, minimumOrderQuantity, productTotalQuantity);
}

// Single entry point for every cart line quantity change (X button and typed quantity).
// Resolves with the updated cart, or with null when the shopper cancelled the removal.
async function requestCartLineQuantityChange(cartFormElement, lineKey, requestedQuantity) {
  const lineElement = Array.from(cartFormElement.querySelectorAll('.js-cart-line'))
    .find((candidateLine) => candidateLine.dataset.key === lineKey);

  if (!lineElement) {
    throw new Error(`Cart line ${lineKey} was not found in the cart form.`);
  }

  const productGroupElement = lineElement.closest('.js-cart-product-group');
  const minimumOrderQuantity = Number.parseInt(productGroupElement.dataset.minimumOrderQuantity, 10);
  const cartLines = readCartLines(productGroupElement);
  const productTotalQuantity = sumProductQuantity(cartLines, productGroupElement.dataset.productId);
  const changedLine = cartLines.find((cartLine) => cartLine.lineKey === lineKey);

  if (!wouldBreakProductMinimum(minimumOrderQuantity, productTotalQuantity, changedLine.quantity, requestedQuantity)) {
    return theme.cart.changeItem(lineKey, requestedQuantity);
  }

  const isRemovalConfirmed = await askToConfirmProductRemoval(
    cartFormElement,
    productGroupElement,
    cartLines.length,
    productTotalQuantity
  );

  if (!isRemovalConfirmed) {
    restoreProductQuantities(productGroupElement);
    return null;
  }

  const updatedCart = await theme.cart.removeItems(cartLines.map((cartLine) => cartLine.lineKey));

  // Set only after a successful removal so a failed one cannot leak the flag into a later render.
  shouldRestoreFocusAfterRender = true;

  return updatedCart;
}

// Removing the whole product leaves 0 units, which the minimum order rule always allows: no confirmation.
async function requestCartProductRemoval(productGroupElement) {
  const lineKeys = readCartLines(productGroupElement).map((cartLine) => cartLine.lineKey);
  const updatedCart = await theme.cart.removeItems(lineKeys);

  // Set only after a successful removal so a failed one cannot leak the flag into a later render.
  shouldRestoreFocusAfterRender = true;

  return updatedCart;
}
